import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";

// Formate les 9 chiffres sans le 0 initial : 6 12 34 56 78
function formatFrenchLocalDigits(val) {
  if (!val) return "";
  let digits = val.replace(/\D/g, "");
  if (digits.startsWith("0")) digits = digits.slice(1);
  digits = digits.slice(0, 9);
  if (digits.length <= 1) return digits;
  const first = digits[0];
  const rest = digits.slice(1);
  const parts = rest.match(/.{1,2}/g) || [];
  return `${first} ${parts.join(" ")}`.trim();
}

const EMPTY_CUSTOMER = { first_name: "", last_name: "", email: "", phone: "" };

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");

  // État d'ajout manuel
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_CUSTOMER);
  const [saving, setSaving] = useState(false);

  // État de confirmation de suppression
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    API.get("/customers")
      .then((r) => setCustomers(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const rawDigits = form.phone ? form.phone.replace(/\D/g, "") : "";
    if (!form.first_name.trim()) {
      toast.error("Le prénom est obligatoire");
      return;
    }
    if (!form.last_name.trim()) {
      toast.error("Le nom est obligatoire");
      return;
    }
    if (!rawDigits || rawDigits.length !== 9) {
      toast.error("Numéro de téléphone incomplet (9 chiffres requis après +33)");
      return;
    }

    setSaving(true);
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      phone: `+33${rawDigits}`,
    };

    try {
      await API.post("/customers", payload);
      toast.success("Client créé avec succès");
      setOpenCreate(false);
      setForm(EMPTY_CUSTOMER);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    setDeleting(true);
    try {
      await API.delete(`/customers/${customerToDelete.id}`);
      toast.success("Client supprimé avec succès");
      setCustomerToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = (customers || []).filter((c) =>
    `${c.first_name} ${c.last_name} ${c.phone} ${c.email || ""}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">
            Base clients de votre entreprise. Une fiche unique par numéro de téléphone.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
            data-testid="customers-search"
          />
          <Button
            onClick={() => {
              setForm(EMPTY_CUSTOMER);
              setOpenCreate(true);
            }}
            data-testid="btn-new-customer"
          >
            <Plus className="mr-2 h-4 w-4" /> Nouveau client
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[640px] text-sm" data-testid="customers-table">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Téléphone</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">RDV</th>
              <th className="px-4 py-3">Client depuis</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {customers === null ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  Chargement...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="customers-empty">
                  Aucun client.
                </td>
              </tr>
            ) : (
              filtered.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {c.first_name} {c.last_name}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.phone}</td>
                  <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {c.bookings_count ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => setCustomerToDelete(c)}
                      data-testid={`btn-delete-customer-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal d'ajout manuel de client */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-md" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Nouveau client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prénom *</Label>
                <Input
                  placeholder="Jean"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  placeholder="Dupont"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Téléphone *</Label>
              <div className="mt-1 flex rounded-md border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-slate-900 focus-within:ring-offset-1">
                <span className="inline-flex items-center rounded-l-md border-r border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-600">
                  🇫🇷 +33
                </span>
                <Input
                  type="tel"
                  placeholder="6 12 34 56 78"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: formatFrenchLocalDigits(e.target.value) })}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="jean.dupont@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1"
              />
            </div>

            <Button
              className="w-full mt-2"
              onClick={save}
              disabled={!form.first_name.trim() || !form.last_name.trim() || !form.phone || saving}
              data-testid="customer-form-submit"
            >
              {saving ? "Enregistrement..." : "Créer le client"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alerte de confirmation de suppression */}
      <AlertDialog open={!!customerToDelete} onOpenChange={(open) => !open && setCustomerToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le client{" "}
              <span className="font-semibold text-slate-900">
                {customerToDelete ? `${customerToDelete.first_name} ${customerToDelete.last_name}` : ""}
              </span>{" "}
              sera définitivement supprimé de votre base de données.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Suppression..." : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}